// On-screen joystick for touch screens: a semi-transparent disc in a bottom
// corner (left or right) with a knob that follows the finger. Reports the push
// as x (right +) / y (forward +), each -1..1, with a small dead zone in the
// middle and a gentle curve for fine control near it.
//
// It only handles its own finger (pointer capture). Its touches are stopped
// from reaching the scene's own window-level touch handlers; the scene also
// ignores this element's touches when counting fingers (isJoystickTouch), so
// another finger can drag to look around (or two to pan) while it's held.
// Styled by .touch-joystick in styles.css.

import { isPhoneDevice } from '../Splat.ts';

// localStorage keys for the Settings choices. (Replaced the first keys,
// 'cinesplat-joystick' / 'cinesplat-joystick-look', which an early version
// set to '0' on every Settings Apply — that would hide the phone default.)
export const JOYSTICK_KEYS = { move: 'cinesplat-move-joystick', look: 'cinesplat-look-joystick' };
try { localStorage.removeItem('cinesplat-joystick'); localStorage.removeItem('cinesplat-joystick-look'); } catch (e) { /* storage off */ }

// Saved Settings choice ('1' / '0' in localStorage under `key`); unset means
// the default — on for phones, off for tablets and desktops.
export function isJoystickEnabled(key) {
  try {
    const v = localStorage.getItem(key);
    if (v === '1' || v === '0') return v === '1';
  } catch (e) { /* storage off */ }
  return isPhoneDevice;
}

const SIZE = 112;     // disc diameter, px
const KNOB = 48;      // knob diameter, px
const DEAD_ZONE = 0.12;
const CURVE = 1.5;

/**
 * @param {object} o
 * @param {'left'|'right'} [o.side]  which bottom corner
 * @param {(x: number, y: number) => void} o.onChange  -1..1 each; (0, 0) on release
 * @returns {{ element: HTMLElement, setVisible(v: boolean): void, setBottom(px: number): void, destroy(): void }}
 */
export function createTouchJoystick({ side = 'left', onChange }) {
  const maxTravel = (SIZE - KNOB) / 2;
  const base = document.createElement('div');
  base.className = `touch-joystick ${side}`;
  base.style.width = `${SIZE}px`;
  base.style.height = `${SIZE}px`;
  const knob = document.createElement('div');
  knob.className = 'touch-joystick-knob';
  knob.style.width = `${KNOB}px`;
  knob.style.height = `${KNOB}px`;
  knob.style.margin = `${-KNOB / 2}px 0 0 ${-KNOB / 2}px`;
  base.appendChild(knob);
  document.body.appendChild(base);

  let pointerId = null;
  let cx = 0;
  let cy = 0;

  function emit(dx, dy) {
    const len = Math.hypot(dx, dy);
    const travel = Math.min(len, maxTravel);
    const ux = len ? dx / len : 0;
    const uy = len ? dy / len : 0;
    knob.style.transform = `translate(${ux * travel}px, ${uy * travel}px)`;
    let m = travel / maxTravel;
    m = m <= DEAD_ZONE ? 0 : Math.pow((m - DEAD_ZONE) / (1 - DEAD_ZONE), CURVE);
    onChange(ux * m, -uy * m); // screen up = forward
  }

  function release() {
    pointerId = null;
    base.classList.remove('active');
    knob.style.transform = '';
    onChange(0, 0);
  }

  base.addEventListener('pointerdown', (e) => {
    if (pointerId !== null) return;
    pointerId = e.pointerId;
    try { base.setPointerCapture(pointerId); } catch (err) { /* older browsers */ }
    const r = base.getBoundingClientRect();
    cx = r.left + r.width / 2;
    cy = r.top + r.height / 2;
    base.classList.add('active');
    emit(e.clientX - cx, e.clientY - cy);
    e.preventDefault();
    e.stopPropagation();
  });
  base.addEventListener('pointermove', (e) => {
    if (e.pointerId !== pointerId) return;
    emit(e.clientX - cx, e.clientY - cy);
    e.preventDefault();
    e.stopPropagation();
  });
  const end = (e) => {
    if (e.pointerId !== pointerId) return;
    release();
    e.stopPropagation();
  };
  base.addEventListener('pointerup', end);
  base.addEventListener('pointercancel', end);
  base.addEventListener('lostpointercapture', end);
  // Keep its touches away from the scene's window-level touch handlers
  // (tap / double-tap / pinch) and stop the page scrolling or zooming.
  ['touchstart', 'touchmove', 'touchend', 'touchcancel'].forEach((type) => {
    base.addEventListener(type, (e) => { e.stopPropagation(); if (e.cancelable) e.preventDefault(); }, { passive: false });
  });
  // Safety nets: iPad Safari sometimes never delivers this finger's
  // pointerup / pointercancel (a system or browser gesture takes the touch,
  // the page loses focus mid-touch) and the joystick kept its last push —
  // moving on its own, unstoppable. Any touch ending anywhere (capture phase,
  // before handlers can stop it) re-checks whether a finger is still on the
  // joystick (a touch's target stays where it started), and losing focus or
  // going to the background lets go.
  const recheck = (e) => {
    if (pointerId !== null && ![...e.touches].some((t) => base.contains(t.target))) release();
  };
  const letGo = () => { if (pointerId !== null) release(); };
  const onVisibility = () => { if (document.hidden) letGo(); };
  window.addEventListener('touchend', recheck, true);
  window.addEventListener('touchcancel', recheck, true);
  window.addEventListener('blur', letGo);
  document.addEventListener('visibilitychange', onVisibility);

  return {
    element: base,
    setVisible(v) {
      base.style.display = v ? 'block' : 'none';
      if (!v && pointerId !== null) release();
    },
    setBottom(px) {
      base.style.bottom = `${px}px`;
    },
    destroy() {
      if (pointerId !== null) release();
      window.removeEventListener('touchend', recheck, true);
      window.removeEventListener('touchcancel', recheck, true);
      window.removeEventListener('blur', letGo);
      document.removeEventListener('visibilitychange', onVisibility);
      base.remove();
    },
  };
}
